"use client";

import { ConfigProvider, theme as antdTheme } from "antd";
import { useCallback, useSyncExternalStore } from "react";

// antd paints its own colours through CSS-in-JS, so the `.dark` class and our
// design tokens never reach it: on the report details page alone that left 648
// elements rendering near-black text on a dark surface (ant-collapse, ant-anchor).
//
// This reads the theme off the `.dark` class rather than from ThemeProvider's
// context, because `Providers` is mounted in the ROOT layout while ThemeProvider
// is mounted in the `(user)` layout beneath it, and context only flows down.
// Watching the class avoids reordering the provider tree, which respondent
// routes also depend on.
const subscribe = (onChange: () => void) => {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
  return () => observer.disconnect();
};

const useIsDarkClass = () =>
  useSyncExternalStore(
    subscribe,
    useCallback(() => document.documentElement.classList.contains("dark"), []),
    // Server render assumes light; ThemeProvider's pre-paint script adds the
    // class before hydration, so the observer corrects it on the first commit.
    useCallback(() => false, [])
  );

export const AntdThemeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const isDark = useIsDarkClass();
  return (
    <ConfigProvider
      theme={{
        // Unchanged from before in light mode: same font and brand tokens, no
        // algorithm. Dark adds antd's own dark algorithm and then points its
        // surface/text tokens at ours so the two palettes agree.
        algorithm: isDark ? antdTheme.darkAlgorithm : undefined,
        token: {
          fontFamily: "var(--font-inter)",
          colorPrimary: "#0021cc",
          colorLink: "#2546f0",
          ...(isDark
            ? {
                // #0021cc scores 1.98 against the dark page, which is what made
                // the selection ink in the report rail unreadable.
                //
                // This one MUST be a literal, unlike the tokens below. antd's
                // algorithm derives a whole palette from colorPrimary, so it
                // does colour maths on the value and cannot parse hsl(var(...));
                // passing the token left the ink at a derived #031fb0. The
                // literal is --surface-brand-secondary's dark value (230 100%
                // 68%), so it stays in step with the design tokens.
                colorPrimary: "#5c77ff",
                colorBgContainer: "hsl(var(--surface-highlight))",
                colorBgElevated: "hsl(var(--surface-secondary))",
                colorBgLayout: "hsl(var(--background))",
                colorText: "hsl(var(--content-primary))",
                colorTextSecondary: "hsl(var(--content-tertiary))",
                colorTextTertiary: "hsl(var(--content-tertiary))",
                colorTextDescription: "hsl(var(--content-tertiary))",
                colorBorder: "hsl(var(--border))",
                colorBorderSecondary: "hsl(var(--border))",
                colorLink: "hsl(var(--content-brand))",
              }
            : {}),
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};
